// src/components/InternalOverviewView.js
import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';
import { formatDateTime, getClientDisplayName } from '../utils';
import EisenhowerMatrixView from './EisenhowerMatrixView';
import { ROUTES } from '../constants/routes';
import { DepartmentSelector, StepperFilter, usePersistedFilters, persistFilters, SingleSelectDropdown, ToggleButton, DropdownCheckbox } from './FilterControls';
import SmartFilterBar from './SmartFilterBar';
import { GOAL_STATUS_OPTIONS, INITIATIVE_STATUS_OPTIONS, ISSUE_SEVERITY_OPTIONS, ISSUE_STATUS_OPTIONS, ACTION_ITEM_STATUS_OPTIONS } from '../constants';
import { useEntityNavigationContext } from './EntityNavigation';
import { useStaffContext } from '../contexts/StaffContext';

// Feature flag: Set to false to completely disable Eisenhower Matrix feature
const ENABLE_EISENHOWER_MATRIX = true;

// LocalStorage key for Eisenhower Matrix visibility
const EISENHOWER_VISIBLE_KEY = 'cortex-eisenhower-visible';

const InternalOverviewView = () => {
    const { apiService, departmentId: departmentIdFromContext, dateRange, setDateRange, allClients } = useOutletContext();
    const navigate = useNavigate();
    const params = useParams();
    const { getDriDisplayName } = useStaffContext();
    
    // Get filter expansion state from EntityNavigation context
    const { filtersExpanded } = useEntityNavigationContext();
    
    // Filters are always collapsible (controlled by EntityNavigation header)
    const filterVariant = 'collapsible';
    
    const activeRouteDepartmentId = departmentIdFromContext || params.departmentId;

    const [departments, setDepartments] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState(activeRouteDepartmentId || '');
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    const [priorityCounts, setPriorityCounts] = useState({
        criticalIssues: 0,
        totalIssues: 0,
        blockedInitiatives: 0,
        totalInitiatives: 0,
        overdueActions: 0,
        neglectedGoals: 0,
        totalGoals: 0
    });
    
    const [staffWorkload, setStaffWorkload] = useState([]);
    const [clientHealth, setClientHealth] = useState([]);
    const [recentComms, setRecentComms] = useState([]);

    const [dris, setDris] = useState([]);
    const [selectedDris, setSelectedDris] = usePersistedFilters('selectedDris', []);
    
    // Related Clients filter
    const [relatedClients, setRelatedClients] = useState([]);
    const [selectedClients, setSelectedClients] = usePersistedFilters('selectedClients', []);
    
    // Eisenhower Matrix filters - persisted
    const [minImportanceFilter, setMinImportanceFilter] = usePersistedFilters('minImportance', 0);
    const [minUrgencyFilter, setMinUrgencyFilter] = usePersistedFilters('minUrgency', 0);
    
    // Entity-specific filters for the matrix (persisted)
    const [goalStatusFilter, setGoalStatusFilter] = usePersistedFilters('goalStatusFilter', '');
    const [initiativeStatusFilter, setInitiativeStatusFilter] = usePersistedFilters('initiativeStatusFilter', '');
    const [showBlockedInitiatives, setShowBlockedInitiatives] = usePersistedFilters('showBlockedInitiatives', false);
    const [issueSeverityFilter, setIssueSeverityFilter] = usePersistedFilters('issueSeverityFilter', '');
    const [issueStatusFilter, setIssueStatusFilter] = usePersistedFilters('issueStatusFilter', '');
    const [deadlineFilter, setDeadlineFilter] = usePersistedFilters('deadlineBeforeFilter', '');
    const [actionInitiativeFilter, setActionInitiativeFilter] = usePersistedFilters('actionInitiativeFilter', '');
    const [assigneeFilter, setAssigneeFilter] = usePersistedFilters('assigneeFilter', '');
    const [actionItemStatusFilter, setActionItemStatusFilter] = usePersistedFilters('actionItemStatusFilter', '');
    
    // Initiative list for filter
    const [initiativeListForFilter, setInitiativeListForFilter] = useState([]);
    // Assignee list for filter
    const [assigneeList, setAssigneeList] = useState([]);

    // Eisenhower Matrix visibility - persisted to localStorage (shared across views)
    const [showEisenhowerMatrix, setShowEisenhowerMatrix] = useState(() => {
        const saved = localStorage.getItem(EISENHOWER_VISIBLE_KEY);
        return saved === 'true';
    });
    
    const toggleEisenhowerMatrix = () => {
        setShowEisenhowerMatrix(prev => {
            const newState = !prev;
            localStorage.setItem(EISENHOWER_VISIBLE_KEY, String(newState));
            return newState;
        });
    };

    const hasActiveFilters = selectedDris.length > 0 || minImportanceFilter > 0 || minUrgencyFilter > 0 ||
        goalStatusFilter !== '' || initiativeStatusFilter !== '' || showBlockedInitiatives || 
        issueSeverityFilter !== '' || issueStatusFilter !== '' || deadlineFilter !== '' || 
        actionInitiativeFilter !== '' || assigneeFilter !== '' || selectedClients.length > 0 || actionItemStatusFilter !== '';
    
    const handleClearAllFilters = () => {
        setSelectedDris([]);
        setSelectedClients([]);
        setMinImportanceFilter(0);
        setMinUrgencyFilter(0);
        setGoalStatusFilter('');
        setInitiativeStatusFilter('');
        setShowBlockedInitiatives(false);
        setIssueSeverityFilter('');
        setIssueStatusFilter('');
        setDeadlineFilter('');
        setActionInitiativeFilter('');
        setAssigneeFilter('');
        setActionItemStatusFilter('');
    };
    
    const handleClientChange = (client) => {
        setSelectedClients(prev =>
            prev.includes(client) ? prev.filter(c => c !== client) : [...prev, client]
        );
    };

    const loadDris = useCallback(async () => {
        if (!apiService || !selectedDepartment) return;
        try {
            const driResult = await apiService.getDepartmentDRIs(selectedDepartment);
            setDris(driResult.map(r => r.dri));
        } catch (err) {
            console.error('Error loading DRIs:', err);
        }
    }, [apiService, selectedDepartment]);

    const loadRelatedClients = useCallback(async () => {
        if (!apiService || !selectedDepartment || !dateRange || !dateRange.start || !dateRange.end) return;
        try {
            const clientResult = await apiService.getDepartmentRelatedClients(selectedDepartment, dateRange.start, dateRange.end);
            setRelatedClients(clientResult.map(r => r.clientName).filter(Boolean));
        } catch (err) {
            console.error('Error loading related clients:', err);
        }
    }, [apiService, selectedDepartment, dateRange]);

    const loadFilterOptions = useCallback(async () => {
        if (!apiService || !selectedDepartment || !dateRange || !dateRange.start || !dateRange.end) return;
        try {
            // Load initiatives and assignees for filters
            const actionItems = await apiService.getDepartmentActionItems(selectedDepartment, {
                startDate: dateRange.start,
                endDate: dateRange.end
            });
            
            // Extract unique assignees and initiatives from the action items
            const assignees = [...new Set(actionItems.map(item => item.assigned_to).filter(Boolean))];
            const initiatives = [...new Set(actionItems.map(item => item.initiativeNames).flat().filter(Boolean))];
            
            setAssigneeList(assignees);
            setInitiativeListForFilter(initiatives);
        } catch (err) {
            console.error('Error loading filter options:', err);
        }
    }, [apiService, selectedDepartment, dateRange]);

    useEffect(() => {
        if (selectedDepartment) {
            loadDris();
        }
    }, [selectedDepartment, loadDris]);

    useEffect(() => {
        if (selectedDepartment && dateRange && dateRange.start && dateRange.end) {
            loadRelatedClients();
            loadFilterOptions();
        }
    }, [selectedDepartment, dateRange, loadRelatedClients, loadFilterOptions]);

    const handleDriChange = (dri) => {
        setSelectedDris(prev =>
            prev.includes(dri) ? prev.filter(d => d !== dri) : [...prev, dri]
        );
    };

    useEffect(() => {
        if (activeRouteDepartmentId && activeRouteDepartmentId !== selectedDepartment) {
            setSelectedDepartment(activeRouteDepartmentId);
        }
    }, [activeRouteDepartmentId, selectedDepartment]);

    const loadDepartments = useCallback(async () => {
        if (!apiService) return;
        try {
            const deptResult = await apiService.getDepartmentList();
            const deptList = deptResult.map(r => r.department).filter(d => d);
            setDepartments(deptList);
            
            if (!selectedDepartment && !activeRouteDepartmentId && deptList.length > 0) {
                setSelectedDepartment(deptList[0]);
            } else if (activeRouteDepartmentId && !selectedDepartment && deptList.includes(activeRouteDepartmentId)) {
                setSelectedDepartment(activeRouteDepartmentId);
            }

        } catch (err) {
            console.error('Error loading departments:', err);
            setError(`Failed to load departments: ${err.message}`);
        }
    }, [apiService, selectedDepartment, activeRouteDepartmentId]);

    const loadDepartmentData = useCallback(async () => {
        if (!apiService || !selectedDepartment || !dateRange || !dateRange.start || !dateRange.end) {
            setLoading(false);
            return;
        }
        
        setLoading(true);
        setError(null);
        
        try {
            const [
                countsResult, workloadResult, healthResult, commsResult
            ] = await Promise.all([
                apiService.getDepartmentPriorityCounts(selectedDepartment, dateRange.start, dateRange.end, selectedDris.length > 0 ? selectedDris : null),
                apiService.getDepartmentStaffWorkload(selectedDepartment),
                apiService.getDepartmentClientHealth(selectedDepartment, dateRange.start, dateRange.end),
                apiService.getDepartmentPriorityCommunications(selectedDepartment, dateRange.start, dateRange.end)
            ]);
            
            if (countsResult && countsResult.length > 0) {
                const counts = countsResult[0];
                setPriorityCounts({
                    criticalIssues: counts.criticalIssuesCount || 0,
                    totalIssues: counts.totalIssuesCount || 0,
                    blockedInitiatives: counts.blockedInitiativesCount || 0,
                    totalInitiatives: counts.totalInitiativesCount || 0,
                    overdueActions: counts.overdueActionsCount || 0,
                    neglectedGoals: counts.neglectedGoalsCount || 0,
                    totalGoals: counts.totalGoalsCount || 0
                });
            } else {
                 setPriorityCounts({ criticalIssues: 0, totalIssues: 0, blockedInitiatives: 0, totalInitiatives: 0, overdueActions: 0, neglectedGoals: 0, totalGoals: 0 });
            }
            
            setStaffWorkload(workloadResult || []);
            setClientHealth(healthResult || []);
            setRecentComms(commsResult || []);
            
        } catch (err) {
            console.error(`Error loading data for department ${selectedDepartment}:`, err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [apiService, selectedDepartment, dateRange, selectedDris]);

    useEffect(() => {
        loadDepartments();
    }, [loadDepartments]);

    useEffect(() => {
        if (selectedDepartment && dateRange && dateRange.start && dateRange.end) {
            loadDepartmentData();
        }
    }, [selectedDepartment, dateRange, loadDepartmentData]);

    const handleDepartmentChange = (newDept) => {
        navigate(`${ROUTES.OPERATIONS}/${newDept}/overview`);
    };

    if (!departments.length && !loading && !error) {
      return (
            <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 rounded">
                <p className="font-bold">No Departments Found</p>
                <p className="mt-2">Ensure staff nodes have a 'department' property in Neo4j.</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* SmartFilterBar for entity navigation */}
            <SmartFilterBar
                dateRange={dateRange} onDateRangeChange={setDateRange}
                dris={dris} selectedDris={selectedDris} onDriChange={handleDriChange} driDisplayFormatter={getDriDisplayName}
                filterGroups={[
                    { id: 'context', label: 'Context', filters: [{ key: 'department' }, { key: 'client' }] },
                    { id: 'goals', label: 'Goals & Initiatives', filters: [{ key: 'goalStatus' }, { key: 'initiativeStatus' }, { key: 'blockedInitiatives' }] },
                    { id: 'issues', label: 'Issues', filters: [{ key: 'severity' }, { key: 'issueStatus' }] },
                    { id: 'actions', label: 'Action Items', filters: [{ key: 'deadline' }, { key: 'initiative' }, { key: 'assignee' }, { key: 'actionStatus' }] },
                    { id: 'priority', label: 'Priority Matrix', filters: [{ key: 'minImportance', min: 0 }, { key: 'minUrgency', min: 0 }] }
                ]}
                filters={{ department: selectedDepartment, client: selectedClients, goalStatus: goalStatusFilter, initiativeStatus: initiativeStatusFilter, blockedInitiatives: showBlockedInitiatives, severity: issueSeverityFilter, issueStatus: issueStatusFilter, deadline: deadlineFilter, initiative: actionInitiativeFilter, assignee: assigneeFilter, actionStatus: actionItemStatusFilter, minImportance: minImportanceFilter, minUrgency: minUrgencyFilter }}
                filterConfigs={{
                    department: { label: 'Department', render: () => departments.length > 0 && <DepartmentSelector departments={departments} selectedDepartment={selectedDepartment} onDepartmentChange={handleDepartmentChange} loading={loading} /> },
                    client: { label: 'Client', render: () => <DropdownCheckbox options={relatedClients} selectedOptions={selectedClients} onChange={handleClientChange} title="Client" /> },
                    goalStatus: { label: 'Goal Status', render: () => <SingleSelectDropdown id="goalStatusFilter" label="Goal Status" value={goalStatusFilter} onChange={setGoalStatusFilter} options={GOAL_STATUS_OPTIONS} allLabel="All" /> },
                    initiativeStatus: { label: 'Initiative Status', render: () => <SingleSelectDropdown id="initiativeStatusFilter" label="Init. Status" value={initiativeStatusFilter} onChange={(val) => { setInitiativeStatusFilter(val); if (val) setShowBlockedInitiatives(false); }} options={INITIATIVE_STATUS_OPTIONS} allLabel="All" /> },
                    blockedInitiatives: { label: 'Blocked Initiatives', render: () => <ToggleButton label="Blocked Init. Only" isActive={showBlockedInitiatives} onClick={() => { setShowBlockedInitiatives(!showBlockedInitiatives); if (!showBlockedInitiatives) setInitiativeStatusFilter(''); }} /> },
                    severity: { label: 'Severity', render: () => <SingleSelectDropdown id="severityFilter" label="Severity" value={issueSeverityFilter} onChange={setIssueSeverityFilter} options={ISSUE_SEVERITY_OPTIONS} allLabel="All" /> },
                    issueStatus: { label: 'Issue Status', render: () => <SingleSelectDropdown id="issueStatusFilter" label="Issue Status" value={issueStatusFilter} onChange={setIssueStatusFilter} options={ISSUE_STATUS_OPTIONS} allLabel="All" /> },
                    deadline: { label: 'Deadline', render: () => <SingleSelectDropdown id="deadlineFilter" label="Deadline" value={deadlineFilter} onChange={setDeadlineFilter} options={['overdue']} allLabel="All" capitalize={true} /> },
                    initiative: { label: 'Initiative', render: () => <SingleSelectDropdown id="initiativeFilter" label="Initiative" value={actionInitiativeFilter} onChange={setActionInitiativeFilter} options={initiativeListForFilter} allLabel="All" capitalize={false} /> },
                    assignee: { label: 'Assignee', render: () => <SingleSelectDropdown id="assigneeFilter" label="Assignee" value={assigneeFilter} onChange={setAssigneeFilter} options={assigneeList} allLabel="All" capitalize={false} /> },
                    actionStatus: { label: 'Action Status', render: () => <SingleSelectDropdown id="actionItemStatusFilter" label="Action Status" value={actionItemStatusFilter} onChange={setActionItemStatusFilter} options={ACTION_ITEM_STATUS_OPTIONS} allLabel="All" capitalize={true} /> },
                    minImportance: { label: 'Min Importance', min: 0, render: () => <StepperFilter value={minImportanceFilter} onChange={setMinImportanceFilter} min={0} max={10} step={1} label="Min Importance" tooltip="Filter items in the Priority Matrix by minimum importance score" /> },
                    minUrgency: { label: 'Min Urgency', min: 0, render: () => <StepperFilter value={minUrgencyFilter} onChange={setMinUrgencyFilter} min={0} max={3} step={0.5} label="Min Urgency" tooltip="Filter items in the Priority Matrix by minimum urgency score" /> }
                }}
                activeFilterChips={[
                    ...(selectedClients.length > 0 ? [{ label: 'Clients', value: selectedClients.length === 1 ? selectedClients[0] : `${selectedClients.length} selected`, onRemove: () => setSelectedClients([]) }] : []),
                    ...(goalStatusFilter ? [{ label: 'Goal', value: goalStatusFilter.charAt(0).toUpperCase() + goalStatusFilter.slice(1), onRemove: () => setGoalStatusFilter('') }] : []),
                    ...(initiativeStatusFilter ? [{ label: 'Initiative', value: initiativeStatusFilter.charAt(0).toUpperCase() + initiativeStatusFilter.slice(1), onRemove: () => setInitiativeStatusFilter('') }] : []),
                    ...(showBlockedInitiatives ? [{ label: 'Blocked', value: 'Yes', onRemove: () => setShowBlockedInitiatives(false) }] : []),
                    ...(issueSeverityFilter ? [{ label: 'Severity', value: issueSeverityFilter.charAt(0).toUpperCase() + issueSeverityFilter.slice(1), onRemove: () => setIssueSeverityFilter('') }] : []),
                    ...(issueStatusFilter ? [{ label: 'Issue Status', value: issueStatusFilter.charAt(0).toUpperCase() + issueStatusFilter.slice(1), onRemove: () => setIssueStatusFilter('') }] : []),
                    ...(deadlineFilter ? [{ label: 'Deadline', value: deadlineFilter.charAt(0).toUpperCase() + deadlineFilter.slice(1), onRemove: () => setDeadlineFilter('') }] : []),
                    ...(actionInitiativeFilter ? [{ label: 'Initiative', value: actionInitiativeFilter, onRemove: () => setActionInitiativeFilter('') }] : []),
                    ...(assigneeFilter ? [{ label: 'Assignee', value: assigneeFilter, onRemove: () => setAssigneeFilter('') }] : []),
                    ...(actionItemStatusFilter ? [{ label: 'Action Status', value: actionItemStatusFilter.charAt(0).toUpperCase() + actionItemStatusFilter.slice(1), onRemove: () => setActionItemStatusFilter('') }] : []),
                    ...(minImportanceFilter > 0 ? [{ label: 'Importance', value: `≥ ${minImportanceFilter}`, onRemove: () => setMinImportanceFilter(0) }] : []),
                    ...(minUrgencyFilter > 0 ? [{ label: 'Urgency', value: `≥ ${minUrgencyFilter}`, onRemove: () => setMinUrgencyFilter(0) }] : []),
                ]}
                onClearAll={handleClearAllFilters} hasActiveFilters={hasActiveFilters}
                variant={filterVariant} isExpanded={filtersExpanded}
            />
            
            {error && (
                <div 
                    className="p-4 rounded-md"
                    style={{ 
                        backgroundColor: 'var(--pallium-alert-error-bg)', 
                        borderLeft: '4px solid var(--pallium-error-red)', 
                        color: 'var(--pallium-error-red)' 
                    }}
                >
                    Error: {error}
                </div>
            )}

            {loading && (!departments.length || !selectedDepartment) && (
                 <div className="flex items-center justify-center h-64">
                    <div 
                        className="animate-spin rounded-full h-12 w-12 border-b-2"
                        style={{ borderColor: 'var(--pallium-primary-purple)' }}
                    ></div>
                    <span className="ml-3" style={{ color: 'var(--pallium-text-secondary)' }}>Loading departments...</span>
                </div>
            )}
            {loading && selectedDepartment && (
                 <div className="flex items-center justify-center h-64">
                    <div 
                        className="animate-spin rounded-full h-12 w-12 border-b-2"
                        style={{ borderColor: 'var(--pallium-primary-purple)' }}
                    ></div>
                    <span className="ml-3" style={{ color: 'var(--pallium-text-secondary)' }}>Loading data for {selectedDepartment}...</span>
                </div>
            )}

            {!loading && selectedDepartment && !error && (
                <>
                    {/* Priority Cards - Clickable */}
                    <div 
                        className="rounded-xl p-6"
                        style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
                        data-testid="overall-alignment"
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                            <button 
                                onClick={() => {
                                    persistFilters({ issueSeverityFilter: 'high' });
                                    navigate(`${ROUTES.OPERATIONS}/${selectedDepartment}/issues`);
                                }}
                                className="group flex flex-col p-4 rounded-lg transition-all duration-200 text-left"
                                style={{ 
                                    background: 'linear-gradient(135deg, rgba(252, 57, 57, 0.08), var(--pallium-white))',
                                    border: '2px solid rgba(252, 57, 57, 0.2)'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--pallium-error-red)'}
                                onMouseOut={(e) => e.currentTarget.style.borderColor = 'rgba(252, 57, 57, 0.2)'}
                            >
                                <h3 className="text-sm font-medium mb-1" style={{ color: 'var(--pallium-mute-text)' }}>High Sev. Issues</h3>
                                <p className="text-3xl font-bold" style={{ color: 'var(--pallium-error-red)' }}>
                                    {priorityCounts.criticalIssues} 
                                    <span className="text-lg ml-1" style={{ color: 'var(--pallium-mute-text)' }}>/ {priorityCounts.totalIssues}</span>
                                </p>
                            </button>
                            <button 
                                onClick={() => {
                                    persistFilters({ showBlockedInitiatives: true, initiativeStatusFilter: '' });
                                    navigate(`${ROUTES.OPERATIONS}/${selectedDepartment}/goals`);
                                }}
                                className="group flex flex-col p-4 rounded-lg transition-all duration-200 text-left"
                                style={{ 
                                    background: 'linear-gradient(135deg, rgba(239, 163, 29, 0.08), var(--pallium-white))',
                                    border: '2px solid rgba(239, 163, 29, 0.2)'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--pallium-warning-yellow)'}
                                onMouseOut={(e) => e.currentTarget.style.borderColor = 'rgba(239, 163, 29, 0.2)'}
                            >
                                <h3 className="text-sm font-medium mb-1" style={{ color: 'var(--pallium-mute-text)' }}>Blocked Init.</h3>
                                <p className="text-3xl font-bold" style={{ color: 'var(--pallium-warning-yellow)' }}>
                                    {priorityCounts.blockedInitiatives} 
                                    <span className="text-lg ml-1" style={{ color: 'var(--pallium-mute-text)' }}>/ {priorityCounts.totalInitiatives}</span>
                                </p>
                            </button>
                            <button 
                                onClick={() => {
                                    const today = new Date().toISOString().split('T')[0];
                                    persistFilters({ deadlineBeforeFilter: today });
                                    navigate(`${ROUTES.OPERATIONS}/${selectedDepartment}/action-items`);
                                }}
                                className="group flex flex-col p-4 rounded-lg transition-all duration-200 text-left"
                                style={{ 
                                    background: 'linear-gradient(135deg, rgba(0, 156, 220, 0.08), var(--pallium-white))',
                                    border: '2px solid rgba(0, 156, 220, 0.2)'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--pallium-info-cyan)'}
                                onMouseOut={(e) => e.currentTarget.style.borderColor = 'rgba(0, 156, 220, 0.2)'}
                            >
                                <h3 className="text-sm font-medium mb-1" style={{ color: 'var(--pallium-mute-text)' }}>Overdue Actions</h3>
                                <p className="text-3xl font-bold" style={{ color: 'var(--pallium-info-cyan)' }}>{priorityCounts.overdueActions}</p>
                            </button>
                            <button 
                                onClick={() => {
                                    persistFilters({ statusFilter: 'open', showBlockedInitiatives: false, initiativeStatusFilter: '' });
                                    navigate(`${ROUTES.OPERATIONS}/${selectedDepartment}/goals`);
                                }}
                                className="group flex flex-col p-4 rounded-lg transition-all duration-200 text-left"
                                style={{ 
                                    background: 'linear-gradient(135deg, var(--pallium-purple-0-5), var(--pallium-white))',
                                    border: '2px solid var(--pallium-purple-2)'
                                }}
                                onMouseOver={(e) => e.currentTarget.style.borderColor = 'var(--pallium-primary-purple)'}
                                onMouseOut={(e) => e.currentTarget.style.borderColor = 'var(--pallium-purple-2)'}
                            >
                                <h3 className="text-sm font-medium mb-1" style={{ color: 'var(--pallium-mute-text)' }}>Active Goals</h3>
                                <p className="text-3xl font-bold" style={{ color: 'var(--pallium-primary-purple)' }}>{priorityCounts.totalGoals}</p>
                            </button>
                        </div>
                    </div>
                    
                    {/* Eisenhower Matrix Section - Collapsible */}
                    {ENABLE_EISENHOWER_MATRIX && (
                        <div 
                            className="mt-6 rounded-xl overflow-hidden"
                            style={{ backgroundColor: 'var(--pallium-white)', boxShadow: 'var(--pallium-shadow-md)' }}
                        >
                            <button
                                onClick={toggleEisenhowerMatrix}
                                className="w-full px-4 py-3 md:px-6 md:py-4 flex items-center justify-between text-left transition-colors duration-200"
                                style={{ borderBottom: '1px solid var(--pallium-stroke-light)' }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'var(--pallium-purple-0-5)'}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                            >
                                <div className="flex items-center">
                                    <svg 
                                        className={`h-5 w-5 mr-3 transition-transform duration-200 ${showEisenhowerMatrix ? 'rotate-90' : ''}`}
                                        style={{ color: 'var(--pallium-primary-purple)' }}
                                        xmlns="http://www.w3.org/2000/svg" 
                                        viewBox="0 0 20 20" 
                                        fill="currentColor"
                                    >
                                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                    </svg>
                                    <div>
                                        <h3 className="text-lg font-medium" style={{ color: 'var(--pallium-text-primary)' }}>Priority Matrix</h3>
                                        <p className="text-xs mt-0.5" style={{ color: 'var(--pallium-text-secondary)' }}>Eisenhower prioritization based on urgency and importance</p>
                                    </div>
                                </div>
                            </button>
                            
                            {showEisenhowerMatrix && (
                                <div className="p-4 md:p-6">
                                    <EisenhowerMatrixView 
                                        departmentId={selectedDepartment} 
                                        clientId={null}
                                        typeFilter={['Goal', 'Initiative', 'ActionItem', 'Issue']}
                                        driFilter={selectedDris}
                                        clientFilter={selectedClients}
                                        minImportanceFilter={minImportanceFilter}
                                        minUrgencyFilter={minUrgencyFilter}
                                        goalStatusFilter={goalStatusFilter}
                                        initiativeStatusFilter={initiativeStatusFilter}
                                        issueSeverityFilter={issueSeverityFilter}
                                        issueStatusFilter={issueStatusFilter}
                                        deadlineFilter={deadlineFilter}
                                        actionInitiativeFilter={actionInitiativeFilter}
                                        assigneeFilter={assigneeFilter}
                                        showBlockedInitiatives={showBlockedInitiatives}
                                        actionItemStatusFilter={actionItemStatusFilter}
                                    />
                                </div>
                            )}
                        </div>
                    )}
                    
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
                        <div className="bg-white rounded-lg shadow p-6">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">{selectedDepartment} Staff Workload</h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-3 py-2 text-left font-medium text-gray-500">Staff</th>
                                            <th className="px-3 py-2 text-center font-medium text-gray-500">Total Tasks</th>
                                            <th className="px-3 py-2 text-center font-medium text-gray-500">Overdue</th>
                                            <th className="px-3 py-2 text-center font-medium text-gray-500">Due Soon</th>
                                            <th className="px-3 py-2 text-center font-medium text-gray-500">Clients</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {staffWorkload.map((staff, index) => (
                                            <tr key={staff.staffName || index} className="hover:bg-gray-50">
                                                <td className="px-3 py-2 font-medium">{staff.staffName}</td>
                                                <td className="px-3 py-2 text-center">{staff.totalTasks}</td>
                                                <td className="px-3 py-2 text-center"><span className={`px-2 py-1 rounded text-xs font-medium ${staff.overdueTasks > 0 ? 'bg-red-100 text-red-800' : 'text-gray-500'}`}>{staff.overdueTasks}</span></td>
                                                <td className="px-3 py-2 text-center"><span className={`px-2 py-1 rounded text-xs font-medium ${staff.dueSoonTasks > 0 ? 'bg-yellow-100 text-yellow-800' : 'text-gray-500'}`}>{staff.dueSoonTasks}</span></td>
                                                <td className="px-3 py-2 text-center">{staff.clientCount}</td>
                                            </tr>
                                        ))}
                                        {staffWorkload.length === 0 && <tr><td colSpan="5" className="px-3 py-4 text-center text-gray-500">No staff workload data.</td></tr>}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="bg-white rounded-lg shadow p-6">
                             <h3 className="text-lg font-medium text-gray-900 mb-4">{selectedDepartment} Client Health (Top 10 by lowest score)</h3>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-sm">
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th className="px-3 py-2 text-left font-medium text-gray-500">Client</th>
                                            <th className="px-3 py-2 text-center font-medium text-gray-500">Health</th>
                                            <th className="px-3 py-2 text-center font-medium text-gray-500">Goals</th>
                                            <th className="px-3 py-2 text-center font-medium text-gray-500">Issues</th>
                                            <th className="px-3 py-2 text-center font-medium text-gray-500">Blocked</th>
                                            <th className="px-3 py-2 text-center font-medium text-gray-500">Overdue</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {clientHealth.slice(0, 10).map((client, index) => (
                                            <tr key={client.clientId || index} className="hover:bg-gray-50 cursor-pointer" onClick={() => navigate(`${ROUTES.ACCOUNTS}/${client.clientId}/overview`)}>
                                                <td className="px-3 py-2 font-medium text-blue-600 hover:text-blue-800">{getClientDisplayName(client.clientName, client.clientId, allClients)}</td>
                                                <td className="px-3 py-2 text-center"><span className={`px-2 py-1 rounded text-xs font-medium ${client.healthScore >= 80 ? 'bg-green-100 text-green-800' : client.healthScore >= 60 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>{client.healthScore}%</span></td>
                                                <td className="px-3 py-2 text-center">{client.activeGoals}</td>
                                                <td className="px-3 py-2 text-center"><span className={client.highSeverityIssues > 0 ? 'text-red-600 font-medium' : ''}>{client.highSeverityIssues}</span></td>
                                                <td className="px-3 py-2 text-center"><span className={client.blockedInitiatives > 0 ? 'text-orange-600 font-medium' : ''}>{client.blockedInitiatives}</span></td>
                                                <td className="px-3 py-2 text-center"><span className={client.overdueActions > 0 ? 'text-yellow-600 font-medium' : ''}>{client.overdueActions}</span></td>
                                            </tr>
                                        ))}
                                        {clientHealth.length === 0 && <tr><td colSpan="6" className="px-3 py-4 text-center text-gray-500">No client health data.</td></tr>}
                                    </tbody>
                                </table>
                                {clientHealth.length > 10 && <p className="text-xs text-gray-500 mt-2 text-center">Showing 10 of {clientHealth.length} clients.</p>}
                            </div>
                        </div>
                    </div>

                     <div className="bg-white rounded-lg shadow p-6 mt-6">
                        <h3 className="text-lg font-medium text-gray-900 mb-4">Recent Priority Communications - {selectedDepartment}</h3>
                        <div className="space-y-3 max-h-96 overflow-y-auto">
                            {recentComms.map(comm => (
                                <div key={comm.id} className="border rounded-lg p-3 hover:bg-gray-50">
                                    <div className="flex justify-between items-start mb-2">
                                        <div>
                                            {(comm.clientName || comm.clientId) && (
                                                <>
                                                    <span className="font-medium text-blue-600 hover:text-blue-800 cursor-pointer" onClick={() => navigate(`${ROUTES.ACCOUNTS}/${comm.clientId}/sentiment`)}>{getClientDisplayName(comm.clientName, comm.clientId, allClients)}</span>
                                                    <span className="mx-2 text-gray-400">•</span>
                                                </>
                                            )}
                                            <span className="text-sm text-gray-600">{comm.type}</span>
                                            <span className="mx-2 text-gray-400">•</span>
                                            <span className="text-sm text-gray-600">{comm.staff}</span>
                                        </div>
                                        <span className="text-xs text-gray-500">{formatDateTime(comm.date)}</span>
                                    </div>
                                    <h4 className="font-medium mb-1">{comm.title || 'No Title'}</h4>
                                    <p className="text-sm text-gray-700 mb-2">{comm.summary}</p>
                                    <div className="flex gap-4 text-xs">
                                        {comm.highSeverityIssues > 0 && (<span className="text-red-600">High Severity Issues: {comm.highSeverityIssues}</span>)}
                                        {comm.blockedInitiatives > 0 && (<span className="text-orange-600">Blocked Initiatives: {comm.blockedInitiatives}</span>)}
                                    </div>
                                </div>
                            ))}
                            {recentComms.length === 0 && <p className="text-gray-500 text-sm">No recent priority communications found.</p>}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default InternalOverviewView;